import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Check, Lock, MessageSquare, RotateCcw, UserPlus } from 'lucide-react'
import {
  useDeleteMessage,
  useEditMessage,
  useInviteParticipants,
  useMarkRead,
  useMessages,
  usePostMessage,
  useSetThreadStatus,
  useUserNames,
  useUserSearch,
} from '../hooks'
import type { CollabUser, Thread } from '../types'
import { useCollabRealtime } from '../realtime'
import { Composer } from './Composer'
import { MessageItem } from './MessageItem'

function getStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

export function ThreadView({ thread, currentUserId }: {
  thread: Thread
  currentUserId: number
}) {
  // Realtime per entityRef: gdy WS aktywny, wylacza polling i odswieza przez invalidacje.
  const { live } = useCollabRealtime(thread.entityRef)
  const { data: messages, isLoading } = useMessages(thread.id, live)
  const names = useUserNames(
    messages ? messages.flatMap(m => [m.authorUserId, ...m.mentions]) : [],
  )
  const setStatus = useSetThreadStatus()
  const postMessage = usePostMessage()
  const editMessage = useEditMessage()
  const deleteMessage = useDeleteMessage()
  const markRead = useMarkRead()

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editBody, setEditBody] = useState('')

  // Oznacz jako przeczytane po załadowaniu wiadomości - keyed na id ostatniej (najnowszej) wiadomości,
  // żeby nie wpaść w pętlę przy każdym renderze.
  const lastMessageId = messages && messages.length > 0 ? messages[messages.length - 1].id : null
  const markReadMutate = markRead.mutate
  useEffect(() => {
    if (lastMessageId != null) {
      markReadMutate({ threadId: thread.id, lastReadMessageId: lastMessageId })
    }
  }, [lastMessageId, thread.id, markReadMutate])

  function toggleStatus() {
    const next = thread.status === 'OPEN' ? 'RESOLVED' : 'OPEN'
    setStatus.mutate({ threadId: thread.id, status: next })
  }

  function startEdit(id: number) {
    const msg = messages?.find(m => m.id === id)
    setEditingId(id)
    setEditBody(msg?.body ?? '')
  }

  function saveEdit() {
    if (editingId == null) return
    const messageId = editingId
    editMessage.mutate(
      { messageId, input: { body: editBody } },
      {
        onSuccess: () => setEditingId(null),
        onError: (err) => {
          if (getStatus(err) === 409) {
            toast.error('Okno edycji minęło')
          } else {
            toast.error('Nie udało się zapisać zmiany')
          }
        },
      },
    )
  }

  function handleDelete(id: number) {
    if (!window.confirm('Usunąć tę wiadomość?')) return
    deleteMessage.mutate(id)
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-2.5">
        <h3 className="flex flex-1 items-center gap-1.5 truncate font-semibold text-slate-800">
          {thread.visibility === 'PRIVATE' && (
            <Lock className="h-4 w-4 shrink-0 text-slate-400" aria-label="Prywatny" />
          )}
          <span className="truncate">{thread.title}</span>
        </h3>
        <InviteAffordance thread={thread} />
        <button
          type="button"
          onClick={toggleStatus}
          className={[
            'inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
            thread.status === 'OPEN'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50',
          ].join(' ')}
        >
          {thread.status === 'OPEN' ? (
            <>
              <Check className="h-3.5 w-3.5" /> Oznacz rozwiązany
            </>
          ) : (
            <>
              <RotateCcw className="h-3.5 w-3.5" /> Otwórz ponownie
            </>
          )}
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-auto py-1">
        {isLoading ? (
          <p className="px-4 py-4 text-sm text-slate-400">Ładowanie…</p>
        ) : !messages || messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <MessageSquare className="h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-400">Brak wiadomości - rozpocznij dyskusję</p>
          </div>
        ) : (
          messages.map(message =>
            editingId === message.id ? (
              <div key={message.id} className="border-b px-3 py-2">
                <textarea
                  value={editBody}
                  onChange={e => setEditBody(e.target.value)}
                  rows={3}
                  className="w-full resize-none rounded border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
                <div className="mt-1 flex gap-3 text-xs">
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={!editBody.trim()}
                    className="text-sky-600 hover:underline disabled:text-slate-300"
                  >
                    Zapisz
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="text-slate-500 hover:underline"
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <MessageItem
                key={message.id}
                message={message}
                currentUserId={currentUserId}
                names={names}
                onEdit={startEdit}
                onDelete={handleDelete}
              />
            ),
          )
        )}
      </div>

      <Composer
        onSend={({ body, mentions, attachmentIds }) =>
          postMessage.mutate({ threadId: thread.id, input: { body, mentions, attachmentIds } })
        }
      />
    </div>
  )
}

function InviteAffordance({ thread }: { thread: Thread }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const { data: results } = useUserSearch(query)
  const invite = useInviteParticipants()
  const ref = useRef<HTMLDivElement>(null)

  const showDropdown = query.trim().length >= 2 && (results?.length ?? 0) > 0

  function pick(user: CollabUser) {
    invite.mutate({ threadId: thread.id, userIds: [user.id] })
    setQuery('')
    setOpen(false)
  }

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
      >
        <UserPlus className="h-3.5 w-3.5" />
        Zaproś
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-10 w-56 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Szukaj użytkownika…"
            className="w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
          {showDropdown && (
            <ul className="mt-1 max-h-48 overflow-auto text-sm">
              {results!.map(u => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => pick(u)}
                    className="w-full rounded px-2 py-1 text-left hover:bg-sky-50"
                  >
                    {u.fullName}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
